import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, Badge, Button, Modal, Input, Select } from '@getpay93/ui';
import { useClient, useClientPlans, usePlans, useInvoices, usePauseMembership, useResumeMembership, useAssignPlan, useRegisterPayment } from '../../data/hooks';
import { money, shortDate } from '../../lib/format';

export function MiembroDetalle() {
  const { id = '' } = useParams();
  const client = useClient(id);
  const plans = useClientPlans(id);
  const allPlans = usePlans();
  const invoices = useInvoices({ clientId: id });
  const pause = usePauseMembership();
  const resume = useResumeMembership();
  const assign = useAssignPlan();
  const pay = useRegisterPayment();

  const [modal, setModal] = useState<null | 'pause' | 'resume' | 'assign' | 'pay'>(null);
  const [activeCp, setActiveCp] = useState<string>('');
  const [pausedUntil, setPausedUntil] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [planId, setPlanId] = useState('');
  const [payInvoiceId, setPayInvoiceId] = useState('');
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState('efectivo');

  function close() {
    setModal(null);
    setActiveCp('');
    setPausedUntil('');
    setReason('');
    setNote('');
    setPlanId('');
    setPayInvoiceId('');
    setPayAmount('');
    setPayMethod('efectivo');
    assign.reset();
    pay.reset();
  }
  const assignError = assign.error instanceof Error ? assign.error.message : null;

  // Un cliente sólo puede tener un plan vigente (active o paused): asignar otro lo cambia.
  const current = (plans.data ?? []).find((cp) => cp.status === 'active' || cp.status === 'paused');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Link to="/app/clientes" style={{ fontSize: 13 }}>← Volver</Link>
      <Card>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: 'var(--text-strong)' }}>{client.data?.name ?? '…'}</h2>
        <div style={{ color: 'var(--text-hint)', fontSize: 14 }}>{client.data?.email ?? client.data?.phone ?? ''}</div>
        <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
          <Button size="sm" onClick={() => setModal('assign')}>{current ? 'Cambiar plan' : 'Asignar plan'}</Button>
          <Button size="sm" variant="outline" onClick={() => setModal('pay')}>Registrar pago</Button>
        </div>
      </Card>

      <Card>
        <strong style={{ color: 'var(--text-strong)' }}>Planes</strong>
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {(plans.data ?? []).map((cp) => (
            <div key={cp.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--border)', paddingTop: 10 }}>
              <div>
                <div style={{ fontWeight: 700, color: 'var(--text-body)' }}>{cp.planName}</div>
                <div style={{ fontSize: 13, color: 'var(--text-hint)' }}>Próximo cobro: {shortDate(cp.nextBillingDate)}</div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Badge tone={cp.status === 'active' ? 'paid' : cp.status === 'paused' ? 'pending' : 'neutral'}>{cp.status}</Badge>
                {cp.status === 'paused' ? (
                  <Button size="sm" variant="ghost" onClick={() => { setActiveCp(cp.id); setModal('resume'); }}>Reanudar</Button>
                ) : (
                  <Button size="sm" variant="ghost" onClick={() => { setActiveCp(cp.id); setModal('pause'); }}>Pausar</Button>
                )}
              </div>
            </div>
          ))}
          {plans.data && plans.data.length === 0 ? <span style={{ color: 'var(--text-hint)' }}>Sin planes asignados.</span> : null}
        </div>
      </Card>

      {modal === 'pause' ? (
        <Modal open onClose={close} title="Pausar membresía" footer={<><Button variant="ghost" onClick={close}>Cancelar</Button><Button onClick={() => pause.mutate({ clientPlanId: activeCp, pausedUntil: pausedUntil || undefined, reason: reason || undefined, note: note || undefined }, { onSuccess: close })}>Confirmar pausa</Button></>}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Input label="Reanudar el (opcional)" type="date" value={pausedUntil} onChange={(e) => setPausedUntil(e.currentTarget.value)} />
            <Input label="Motivo" value={reason} onChange={(e) => setReason(e.currentTarget.value)} />
            <Input label="Nota" value={note} onChange={(e) => setNote(e.currentTarget.value)} />
          </div>
        </Modal>
      ) : null}

      {modal === 'resume' ? (
        <Modal open onClose={close} title="Reanudar membresía" footer={<><Button variant="ghost" onClick={close}>Cancelar</Button><Button onClick={() => resume.mutate({ clientPlanId: activeCp, note: note || undefined }, { onSuccess: close })}>Reanudar</Button></>}>
          <Input label="Nota (opcional)" value={note} onChange={(e) => setNote(e.currentTarget.value)} />
        </Modal>
      ) : null}

      {modal === 'assign' ? (
        <Modal open onClose={close} title={current ? 'Cambiar plan' : 'Asignar plan'} footer={<><Button variant="ghost" onClick={close}>Cancelar</Button><Button disabled={!planId} onClick={() => assign.mutate({ clientId: id, planId }, { onSuccess: close })}>{current ? 'Cambiar' : 'Asignar'}</Button></>}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {current ? (
              <p style={{ margin: 0, fontSize: 13, color: 'var(--text-hint)' }}>
                Se cancelará «{current.planName}» y sus cobros pendientes. El nuevo plan empieza hoy.
              </p>
            ) : null}
            <Select label="Plan" options={[{ value: '', label: 'Elige un plan' }, ...(allPlans.data ?? []).map((p) => ({ value: p.id, label: `${p.name} · ${money(p.price)}` }))]} value={planId} onChange={(e) => setPlanId(e.currentTarget.value)} />
            {assignError ? <p style={{ margin: 0, color: 'var(--status-overdue)', fontSize: 13, fontWeight: 600 }}>{assignError}</p> : null}
          </div>
        </Modal>
      ) : null}

      {modal === 'pay' ? (
        <Modal open onClose={close} title="Registrar pago" footer={<><Button variant="ghost" onClick={close}>Cancelar</Button><Button disabled={!payInvoiceId || !payAmount} onClick={() => pay.mutate({ invoiceId: payInvoiceId, amount: Number(payAmount), method: payMethod }, { onSuccess: close })}>Registrar</Button></>}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Select label="Factura" options={[{ value: '', label: 'Elige una factura pendiente' }, ...(invoices.data ?? []).filter((i) => i.status !== 'paid').map((i) => ({ value: i.id, label: `${money(i.amount)} · vence ${shortDate(i.dueDate)}` }))]} value={payInvoiceId} onChange={(e) => { setPayInvoiceId(e.currentTarget.value); const inv = invoices.data?.find((x) => x.id === e.currentTarget.value); if (inv) setPayAmount(String(inv.amount)); }} />
            <Input label="Monto" type="number" value={payAmount} onChange={(e) => setPayAmount(e.currentTarget.value)} />
            <Select label="Método" options={['efectivo', 'transferencia', 'deuna', 'otro']} value={payMethod} onChange={(e) => setPayMethod(e.currentTarget.value)} />
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
